import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Chip,
  TablePagination,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { EnvVariable } from '../types/envVariables';
import { envVariableIdentityKey, normalizeEnvVariableScope } from '../utils/executionEnvironment';
import { DFT_BRAND } from '../styles/brandPalette';

interface EnvVariableTableProps {
  variables: EnvVariable[];
  onEdit: (variable: EnvVariable) => void;
  onDelete: (variable: EnvVariable) => void;
}

const ENV_TABLE_CARD_SX = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: '14px !important',
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;

const headCellSx = {
  fontWeight: 600,
  fontSize: '0.875rem',
  color: DFT_BRAND.text,
  borderColor: DFT_BRAND.line,
  bgcolor: DFT_BRAND.tint,
  py: 1,
} as const;

const bodyCellSx = {
  py: 1,
  borderColor: DFT_BRAND.line,
  fontSize: '0.875rem',
} as const;

const iconBtnSx = {
  width: 32,
  height: 32,
  p: 0.5,
} as const;

const EnvVariableTable: React.FC<EnvVariableTableProps> = ({
  variables,
  onEdit,
  onDelete,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleValues, setVisibleValues] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleValueVisibility = (key: string) => {
    const newVisible = new Set(visibleValues);
    if (newVisible.has(key)) {
      newVisible.delete(key);
    } else {
      newVisible.add(key);
    }
    setVisibleValues(newVisible);
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(`{{${text}}}`);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const filteredVariables = variables.filter(v =>
    v.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedVariables = filteredVariables.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const maskValue = (value: string): string => {
    if (!value) return '****';
    if (value.length <= 4) return '****';
    return '*'.repeat(value.length);
  };

  if (variables.length === 0) {
    return (
      <Card elevation={0} sx={{ ...ENV_TABLE_CARD_SX, p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text, mb: 0.5 }}>
          No Environment Variables
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Click &quot;Add Variable&quot; to create your first environment variable
        </Typography>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={ENV_TABLE_CARD_SX}>
      <CardContent sx={{ width: '100%', maxWidth: '100%', minWidth: 0, p: '12px !important' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search variables..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#fff',
              height: 40,
              fontSize: '0.875rem',
              '& fieldset': { borderColor: DFT_BRAND.line },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer
          component={Paper}
          className="projects-table-container"
          sx={{
            boxShadow: 'none',
            width: '100%',
            maxWidth: '100%',
            minWidth: '100%',
            overflowX: 'auto',
            display: 'block',
          }}
        >
          <Table
            className="projects-table"
            sx={{
              width: '100%',
              minWidth: { xs: 420, md: '100%' },
              maxWidth: '100%',
              tableLayout: 'fixed',
              display: 'table',
              '& .MuiTableCell-root': {
                fontSize: '0.875rem',
                lineHeight: 1.45,
              },
            }}
          >
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col />
              <col style={{ width: 108 }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Key</TableCell>
                <TableCell sx={headCellSx}>Value</TableCell>
                <TableCell sx={{ ...headCellSx, display: { xs: 'none', sm: 'table-cell' } }}>Scope</TableCell>
                <TableCell sx={{ ...headCellSx, display: { xs: 'none', lg: 'table-cell' } }}>Description</TableCell>
                <TableCell sx={{ ...headCellSx, display: { xs: 'none', xl: 'table-cell' } }}>Last Updated</TableCell>
                <TableCell
                  align="center"
                  sx={{
                    ...headCellSx,
                    width: 108,
                    minWidth: 108,
                    maxWidth: 108,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVariables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={bodyCellSx}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No variables match your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVariables.map((variable) => {
                  const rowId = envVariableIdentityKey(variable.key, variable.scope);
                  const isVisible = visibleValues.has(rowId);
                  const isCopied = copiedKey === rowId;

                  return (
                    <TableRow key={rowId} hover>
                      <TableCell sx={{ ...bodyCellSx, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              color: '#1d4ed8',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                            title={variable.key}
                          >
                            {variable.key}
                          </Typography>
                          <Tooltip title={isCopied ? 'Copied!' : 'Copy variable reference'}>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(variable.key, rowId)}
                              color={isCopied ? 'success' : 'default'}
                              sx={iconBtnSx}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...bodyCellSx, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                              flex: 1,
                            }}
                            title={isVisible ? variable.value : undefined}
                          >
                            {isVisible ? variable.value : maskValue(variable.value)}
                          </Typography>
                          <Tooltip title={isVisible ? 'Hide value' : 'Show value'}>
                            <IconButton
                              size="small"
                              onClick={() => toggleValueVisibility(rowId)}
                              sx={iconBtnSx}
                            >
                              {isVisible
                                ? <VisibilityIcon sx={{ fontSize: 16 }} />
                                : <VisibilityOffIcon sx={{ fontSize: 16 }} />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell sx={{ ...bodyCellSx, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          size="small"
                          label={
                            normalizeEnvVariableScope(variable.scope) === 'project'
                              ? 'Project'
                              : 'Environment'
                          }
                          variant={
                            normalizeEnvVariableScope(variable.scope) === 'project'
                              ? 'outlined'
                              : 'filled'
                          }
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          display: { xs: 'none', lg: 'table-cell' },
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={variable.description || undefined}
                        >
                          {variable.description || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell
                        sx={{
                          ...bodyCellSx,
                          display: { xs: 'none', xl: 'table-cell' },
                          overflow: 'hidden',
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {new Date(variable.updatedAt).toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true,
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          ...bodyCellSx,
                          width: 108,
                          minWidth: 108,
                          maxWidth: 108,
                          px: 0.5,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', justifyContent: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(variable)}
                              color="primary"
                              sx={iconBtnSx}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(variable)}
                              color="error"
                              sx={iconBtnSx}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredVariables.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredVariables.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: DFT_BRAND.muted,
              borderTop: `1px solid ${DFT_BRAND.line}`,
              overflow: 'hidden',
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                minHeight: 36,
                px: 0.5,
                gap: 0.5,
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: DFT_BRAND.muted,
                fontSize: '0.8125rem',
                m: 0,
              },
              '& .MuiTablePagination-select': {
                fontSize: '0.8125rem',
              },
              '& .MuiTablePagination-actions .MuiIconButton-root': {
                p: 0.5,
              },
            }}
          />
        )}

        {searchQuery && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              Showing {filteredVariables.length} of {variables.length} variables
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EnvVariableTable;
